<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import generatePayload from 'dashboard/helper/filterQueryGenerator';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AudienceConditionsBlock from './AudienceConditionsBlock.vue';
import { contactDisplayName } from './contactDisplayName';
import { useAudiencePreview } from './useAudiencePreview';

const props = defineProps({
  audienceData: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const ENTITIES = ['contact', 'company', 'deal'];
const NAME_MAX_LENGTH = 255;

const { t } = useI18n();

const dialogRef = ref(null);
const includeBlockRef = ref(null);
const excludeBlockRef = ref(null);

const nameTouched = ref(false);
const form = ref({ name: '' });

const makeEmptyCriteria = () => ({ include: [], exclude: [] });
const criteriaByEntity = ref({
  contact: makeEmptyCriteria(),
  company: makeEmptyCriteria(),
  deal: makeEmptyCriteria(),
});

const contactLabelsStore = useContactLabelsStore();
const labelColorMap = computed(() => contactLabelsStore.getLabelColorMap);

const {
  result: previewResult,
  error: previewError,
  isLoading: isPreviewPending,
  schedule: schedulePreview,
  reset: resetPreview,
} = useAudiencePreview();
const hiddenContactsCount = computed(
  () => previewResult.value.count - previewResult.value.contacts.length
);
const hasAnyCondition = computed(() =>
  ENTITIES.some(
    entity =>
      criteriaByEntity.value[entity].include.length > 0 ||
      criteriaByEntity.value[entity].exclude.length > 0
  )
);

const isNameValid = computed(() => form.value.name.trim().length > 0);
const nameErrorMessage = computed(() =>
  nameTouched.value && !isNameValid.value ? t('AUDIENCES.FORM.NAME_ERROR') : ''
);

const dialogTitle = computed(() =>
  props.audienceData
    ? t('AUDIENCES.FORM.TITLE_EDIT')
    : t('AUDIENCES.FORM.TITLE_CREATE')
);

// `payload` arrives snake_case (attribute_key, filter_operator, ...), the
// exact shape ConditionRow.vue/ConditionsEditor.vue already round-trip for
// saved Contacts segments - camelCasing it back is enough to re-hydrate the
// editor, option pickers resolve their own display labels from the ids.
const camelizeConditions = payload => useCamelCase(payload || [], {});

const buildCriteriaByEntity = criteria => ({
  contact: {
    include: camelizeConditions(criteria?.include?.contact?.payload),
    exclude: camelizeConditions(criteria?.exclude?.contact?.payload),
  },
  company: {
    include: camelizeConditions(criteria?.include?.company?.payload),
    exclude: camelizeConditions(criteria?.exclude?.company?.payload),
  },
  deal: {
    include: camelizeConditions(criteria?.include?.deal?.payload),
    exclude: camelizeConditions(criteria?.exclude?.deal?.payload),
  },
});

// Each non-empty condition list becomes `{ payload: [...] }` in the shape the
// shared filter engine expects; an entity with no conditions is left out so
// the backend falls back to its defaults (include everything / exclude
// nothing) for that entity.
const buildEntityBlock = conditions => {
  if (!conditions?.length) return null;
  const snakeCased = useSnakeCase(JSON.parse(JSON.stringify(conditions)));
  return generatePayload(snakeCased);
};

const buildCriteria = () => {
  const criteria = { include: {}, exclude: {} };
  ENTITIES.forEach(entity => {
    const includeBlock = buildEntityBlock(
      criteriaByEntity.value[entity].include
    );
    const excludeBlock = buildEntityBlock(
      criteriaByEntity.value[entity].exclude
    );
    if (includeBlock) criteria.include[entity] = includeBlock;
    if (excludeBlock) criteria.exclude[entity] = excludeBlock;
  });
  return criteria;
};

// The preview only runs while the dialog is open (the form is mounted with its
// page, long before anyone opens it).
const isOpen = ref(false);

const triggerPreview = () => {
  if (!isOpen.value) return;
  if (!hasAnyCondition.value) {
    resetPreview();
    return;
  }
  schedulePreview(buildCriteria);
};

watch(criteriaByEntity, triggerPreview, { deep: true });

const syncForm = audience => {
  nameTouched.value = false;
  form.value = { name: audience?.name || '' };
  criteriaByEntity.value = buildCriteriaByEntity(audience?.criteria);
};

watch(() => props.audienceData, syncForm, { immediate: true });

// `audienceData` may still be the previous value here (the parent sets it in the
// same tick it opens the dialog); the watcher above re-syncs the form and the
// criteria watcher re-schedules the preview once the new value arrives.
const onOpen = () => {
  isOpen.value = true;
  resetPreview();
  triggerPreview();
};

const onClose = () => {
  isOpen.value = false;
  resetPreview();
  syncForm(props.audienceData);
};

// Enter in a text field would submit (and save) the whole form; only the Save button does.
const blockImplicitSubmit = event => {
  if (event.target.tagName === 'INPUT') event.preventDefault();
};

const contactLabel = contact => contactDisplayName(contact, t);

const validate = () => {
  nameTouched.value = true;
  const areBlocksValid = [includeBlockRef, excludeBlockRef].every(
    blockRef => blockRef.value?.validate?.() ?? true
  );
  return isNameValid.value && areBlocksValid;
};

const handleDialogConfirm = () => {
  if (!validate()) return;
  emit('save', {
    id: props.audienceData?.id,
    name: form.value.name.trim(),
    criteria: buildCriteria(),
  });
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="6xl"
    overflow-y-auto
    :title="dialogTitle"
    :confirm-button-label="t('AUDIENCES.FORM.SAVE')"
    :is-loading="isLoading"
    @confirm="handleDialogConfirm"
    @open="onOpen"
    @close="onClose"
  >
    <div
      data-menu-boundary
      class="grid gap-6 -mx-2 px-2 overflow-y-auto max-h-[calc(100dvh-14rem)]"
      @keydown.enter="blockImplicitSubmit"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('AUDIENCES.FORM.INTRO') }}
      </p>
      <div class="flex flex-col gap-6 lg:flex-row">
        <div class="grid content-start flex-1 min-w-0 gap-6">
          <Input
            v-model="form.name"
            :label="t('AUDIENCES.FORM.NAME_LABEL')"
            :placeholder="t('AUDIENCES.FORM.NAME_PLACEHOLDER')"
            :maxlength="NAME_MAX_LENGTH"
            :message="nameErrorMessage"
            :message-type="nameErrorMessage ? 'error' : 'info'"
            @blur="nameTouched = true"
          />
          <AudienceConditionsBlock
            ref="includeBlockRef"
            v-model="criteriaByEntity"
            direction="include"
            show-shortcuts
            :title="t('AUDIENCES.FORM.INCLUDE_TITLE')"
            :description="t('AUDIENCES.FORM.INCLUDE_DESCRIPTION')"
          />
          <AudienceConditionsBlock
            ref="excludeBlockRef"
            v-model="criteriaByEntity"
            direction="exclude"
            :title="t('AUDIENCES.FORM.EXCLUDE_TITLE')"
            :description="t('AUDIENCES.FORM.EXCLUDE_DESCRIPTION')"
          />
        </div>
        <div
          class="flex flex-col gap-3 p-4 border rounded-lg shrink-0 h-[30rem] lg:w-80 lg:sticky lg:top-0 bg-n-alpha-1 border-n-weak"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('AUDIENCES.FORM.PREVIEW.TITLE') }}
          </span>
          <div
            v-if="!hasAnyCondition"
            class="flex items-center justify-center flex-1 text-center"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('AUDIENCES.FORM.PREVIEW.NO_CONDITIONS') }}
            </p>
          </div>
          <div
            v-else-if="isPreviewPending"
            class="flex flex-col items-center justify-center flex-1 gap-2 text-n-slate-11"
          >
            <Spinner />
            <span class="text-sm">{{
              t('AUDIENCES.FORM.PREVIEW.LOADING')
            }}</span>
          </div>
          <div
            v-else-if="previewError"
            class="flex items-center justify-center flex-1 text-center"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                previewError === 'incomplete'
                  ? t('AUDIENCES.FORM.PREVIEW.INCOMPLETE')
                  : t('AUDIENCES.FORM.PREVIEW.ERROR')
              }}
            </p>
          </div>
          <div
            v-else-if="!previewResult.count"
            class="flex items-center justify-center flex-1 text-center"
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('AUDIENCES.FORM.PREVIEW.EMPTY') }}
            </p>
          </div>
          <template v-else>
            <div class="grid gap-1">
              <p class="mb-0 text-base font-medium text-n-slate-12">
                {{
                  t('AUDIENCES.FORM.PREVIEW.COUNT', {
                    count: previewResult.count,
                  })
                }}
              </p>
              <p
                v-if="previewResult.withoutPhoneCount"
                class="mb-0 text-sm text-n-amber-11"
              >
                {{
                  t('AUDIENCES.FORM.PREVIEW.WITHOUT_PHONE', {
                    count: previewResult.withoutPhoneCount,
                  })
                }}
              </p>
            </div>
            <ul
              class="grid flex-1 min-h-0 gap-2 my-0 overflow-y-auto list-none content-start"
            >
              <li
                v-for="contact in previewResult.contacts"
                :key="contact.id"
                class="flex items-start gap-2 p-2.5 border rounded-lg border-n-weak bg-n-surface-1"
              >
                <Avatar
                  :name="contactLabel(contact)"
                  :src="contact.thumbnail"
                  :size="24"
                  rounded-full
                />
                <div class="grid min-w-0 gap-1">
                  <span class="text-sm truncate text-n-slate-12">
                    {{ contactLabel(contact) }}
                  </span>
                  <span
                    v-if="contact.phoneNumber"
                    class="text-xs truncate text-n-slate-11"
                  >
                    {{ contact.phoneNumber }}
                  </span>
                  <div
                    v-if="contact.labels?.length"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="label in contact.labels"
                      :key="label"
                      class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
                      :style="{
                        backgroundColor: labelColorMap[label] || '#6B7280',
                      }"
                    >
                      {{ label }}
                    </span>
                  </div>
                </div>
              </li>
            </ul>
            <p
              v-if="hiddenContactsCount > 0"
              class="mb-0 text-xs text-n-slate-11"
            >
              {{
                t('AUDIENCES.FORM.PREVIEW.MORE', { count: hiddenContactsCount })
              }}
            </p>
          </template>
        </div>
      </div>
    </div>
  </Dialog>
</template>
